import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { CdsSourceSettingsApi } from "./CDSSourceSettings.Api";
import type { CdsSourceSettingsViewModel } from "./CDSSourceSettings.ViewModel.types";
import type {
  CdsAttribute,
  CdsConfiguration,
  CdsEntityDefinition,
  CdsStatusOption,
} from "./CDSSourceSettings.types";

const NONE_OPTION = "__none";

const toSelectedOption = (value?: string | null): string => {
  return value && value.length > 0 ? value : NONE_OPTION;
};

const toStatusValue = (status?: CdsStatusOption | null): string => {
  return status ? status.value.toString() : NONE_OPTION;
};

export const useCdsSourceSettingsViewModel = (
  model: SourceSettingsModel,
): CdsSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );
  const previousEnvironmentUrlRef = useRef<string | null>(null);

  const [entityDefinitions, setEntityDefinitions] = useState<CdsEntityDefinition[]>(
    [],
  );
  const [statuses, setStatuses] = useState<CdsStatusOption[]>([]);
  const [dateAttributes, setDateAttributes] = useState<CdsAttribute[]>([]);
  const [assignedToAttributes, setAssignedToAttributes] = useState<CdsAttribute[]>(
    [],
  );
  const [attributes, setAttributes] = useState<CdsAttribute[]>([]);

  const [entityDefinitionsLoaded, setEntityDefinitionsLoaded] = useState(false);
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [dateAttributesLoaded, setDateAttributesLoaded] = useState(false);
  const [assignedToAttributesLoaded, setAssignedToAttributesLoaded] = useState(false);
  const [attributesLoaded, setAttributesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const sourceTypeIdNormalized = model.editingConnection.sourceTypeId.trim().toLowerCase();
  const isVisible =
    (sourceTypeIdNormalized === SourceTypeIds.Cds ||
      sourceTypeIdNormalized === SourceTypeIds.AltusLists) &&
    model.editingConnection.isAuthorised;
  const isAuthorisedProfileReady = isVisible && Boolean(model.editingConnection.profileName);

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<CdsConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedEntityDefinitionId = configuration.entityDefinition?.metadataId ?? "";

  useEffect(() => {
    if (!isVisible) {
      previousEnvironmentUrlRef.current = null;
      return;
    }

    const currentEnvironmentUrl = configuration.url ?? "";
    if (previousEnvironmentUrlRef.current === null) {
      previousEnvironmentUrlRef.current = currentEnvironmentUrl;
      return;
    }

    if (previousEnvironmentUrlRef.current === currentEnvironmentUrl) {
      return;
    }

    previousEnvironmentUrlRef.current = currentEnvironmentUrl;

    setStatuses([]);
    setDateAttributes([]);
    setAssignedToAttributes([]);
    setAttributes([]);

    updateConnectionConfiguration<CdsConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        entityDefinition: null,
        statuses: null,
        doneStatus: null,
        dueDateAttribute: null,
        assignedToAttribute: null,
        tagAttributes: null,
      }),
    );
  }, [configuration.url, isVisible]);

  // Reset dependent selections when profile/auth identity changes.
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setEntityDefinitions([]);
    setStatuses([]);
    setDateAttributes([]);
    setAssignedToAttributes([]);
    setAttributes([]);

    updateConnectionConfiguration<CdsConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        entityDefinition: null,
        statuses: null,
        doneStatus: null,
        dueDateAttribute: null,
        assignedToAttribute: null,
        tagAttributes: null,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadEntityDefinitions = async (): Promise<void> => {
      if (!isAuthorisedProfileReady || !model.boardId || !editingConnectionId) {
        setEntityDefinitions([]);
        setEntityDefinitionsLoaded(true);
        return;
      }

      const cdsApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<CdsSourceSettingsApi>(
          SourceTypeIds.Cds,
        );
      if (!cdsApi) {
        setEntityDefinitions([]);
        setEntityDefinitionsLoaded(true);
        return;
      }

      setEntityDefinitionsLoaded(false);
      setHasError(false);
      try {
        setEntityDefinitions(
          await cdsApi.getEntityDefinitions(model.boardId, editingConnectionId),
        );
      } catch {
        setEntityDefinitions([]);
        setHasError(true);
      } finally {
        setEntityDefinitionsLoaded(true);
      }
    };

    void loadEntityDefinitions();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    model.boardId,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadStatusOptions = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedEntityDefinitionId
      ) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      const cdsApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<CdsSourceSettingsApi>(
          SourceTypeIds.Cds,
        );
      if (!cdsApi) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      setStatusesLoaded(false);
      setHasError(false);
      try {
        const loadedStatuses = await cdsApi.getStatusOptions(
          model.boardId,
          editingConnectionId,
          selectedEntityDefinitionId,
        );
        setStatuses(loadedStatuses);

        const defaultStatuses = loadedStatuses.filter((status) => status.state === 0);
        const defaultDoneStatus =
          loadedStatuses.find((status) => status.state === 1) ?? null;

        updateConnectionConfiguration<CdsConfiguration>(
          editingConnectionRef.current,
          onUpdateRef.current,
          // Fills only what is missing, and only for the entity the options were
          // loaded for, so it is safe to apply to the saved settings too. The
          // same holds below.
          (cfg) =>
            (cfg.entityDefinition?.metadataId ?? "") !== selectedEntityDefinitionId
              ? cfg
              : {
                  ...cfg,
                  statuses:
                    cfg.statuses && cfg.statuses.length > 0
                      ? cfg.statuses
                      : defaultStatuses,
                  doneStatus: cfg.doneStatus ?? defaultDoneStatus,
                },
          { isDefault: true },
        );
      } catch {
        setStatuses([]);
        setHasError(true);
      } finally {
        setStatusesLoaded(true);
      }
    };

    void loadStatusOptions();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    model.boardId,
    selectedEntityDefinitionId,
  ]);

  useEffect(() => {
    const loadDateAttributes = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedEntityDefinitionId
      ) {
        setDateAttributes([]);
        setDateAttributesLoaded(true);
        return;
      }

      const cdsApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<CdsSourceSettingsApi>(
          SourceTypeIds.Cds,
        );
      if (!cdsApi) {
        setDateAttributes([]);
        setDateAttributesLoaded(true);
        return;
      }

      setDateAttributesLoaded(false);
      setHasError(false);
      try {
        const loadedDateAttributes = await cdsApi.getDateAttributes(
          model.boardId,
          editingConnectionId,
          selectedEntityDefinitionId,
        );
        setDateAttributes(loadedDateAttributes);

        const defaultDueDateAttribute =
          loadedDateAttributes.find((attribute) =>
            attribute.schemaName?.toLowerCase().includes("scheduledend"),
          ) ?? null;

        updateConnectionConfiguration<CdsConfiguration>(
          editingConnectionRef.current,
          onUpdateRef.current,
          (cfg) =>
            (cfg.entityDefinition?.metadataId ?? "") !== selectedEntityDefinitionId ||
            cfg.dueDateAttribute
              ? cfg
              : { ...cfg, dueDateAttribute: defaultDueDateAttribute },
          { isDefault: true },
        );
      } catch {
        setDateAttributes([]);
        setHasError(true);
      } finally {
        setDateAttributesLoaded(true);
      }
    };

    void loadDateAttributes();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    model.boardId,
    selectedEntityDefinitionId,
  ]);

  useEffect(() => {
    const loadAssignedToAttributes = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedEntityDefinitionId
      ) {
        setAssignedToAttributes([]);
        setAssignedToAttributesLoaded(true);
        return;
      }

      const cdsApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<CdsSourceSettingsApi>(
          SourceTypeIds.Cds,
        );
      if (!cdsApi) {
        setAssignedToAttributes([]);
        setAssignedToAttributesLoaded(true);
        return;
      }

      setAssignedToAttributesLoaded(false);
      setHasError(false);
      try {
        const loadedAssignedToAttributes = await cdsApi.getSystemUserAttributes(
          model.boardId,
          editingConnectionId,
          selectedEntityDefinitionId,
        );
        setAssignedToAttributes(loadedAssignedToAttributes);

        const defaultAssignedToAttribute =
          loadedAssignedToAttributes.find((attribute) =>
            attribute.schemaName?.toLowerCase().includes("owninguser"),
          ) ?? null;

        updateConnectionConfiguration<CdsConfiguration>(
          editingConnectionRef.current,
          onUpdateRef.current,
          (cfg) =>
            (cfg.entityDefinition?.metadataId ?? "") !== selectedEntityDefinitionId ||
            cfg.assignedToAttribute
              ? cfg
              : { ...cfg, assignedToAttribute: defaultAssignedToAttribute },
          { isDefault: true },
        );
      } catch {
        setAssignedToAttributes([]);
        setHasError(true);
      } finally {
        setAssignedToAttributesLoaded(true);
      }
    };

    void loadAssignedToAttributes();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    model.boardId,
    selectedEntityDefinitionId,
  ]);

  useEffect(() => {
    const loadAttributes = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedEntityDefinitionId
      ) {
        setAttributes([]);
        setAttributesLoaded(true);
        return;
      }

      const cdsApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<CdsSourceSettingsApi>(
          SourceTypeIds.Cds,
        );
      if (!cdsApi) {
        setAttributes([]);
        setAttributesLoaded(true);
        return;
      }

      setAttributesLoaded(false);
      setHasError(false);
      try {
        setAttributes(
          await cdsApi.getAttributes(
            model.boardId,
            editingConnectionId,
            selectedEntityDefinitionId,
          ),
        );
      } catch {
        setAttributes([]);
        setHasError(true);
      } finally {
        setAttributesLoaded(true);
      }
    };

    void loadAttributes();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    model.boardId,
    selectedEntityDefinitionId,
  ]);

  return {
    isVisible,
    isAuthorisedProfileReady,
    environmentDisplayName: configuration.orgDisplayName,
    environmentUrl: configuration.url,
    entityDefinitions,
    entityDefinitionsLoaded,
    statuses,
    statusesLoaded,
    dateAttributes,
    dateAttributesLoaded,
    assignedToAttributes,
    assignedToAttributesLoaded,
    attributes,
    attributesLoaded,
    hasError,
    selectedEntityDefinitionId: toSelectedOption(selectedEntityDefinitionId),
    selectedDoneStatusValue: toStatusValue(configuration.doneStatus),
    isDoneStatusInactive: (configuration.doneStatus?.state ?? 0) !== 0,
    selectedDueDateAttributeId: toSelectedOption(
      configuration.dueDateAttribute?.metadataId,
    ),
    selectedAssignedToAttributeId: toSelectedOption(
      configuration.assignedToAttribute?.metadataId,
    ),
    selectedStatusValues: (configuration.statuses ?? []).map((status) =>
      status.value.toString(),
    ),
    selectedTagAttributeIds: (configuration.tagAttributes ?? []).map(
      (attribute) => attribute.metadataId,
    ),
    noneOptionValue: NONE_OPTION,

    onSelectEntityDefinition: (metadataId?: string) => {
      const selectedEntityDefinition =
        entityDefinitions.find((entity) => entity.metadataId === metadataId) ?? null;
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          entityDefinition: selectedEntityDefinition,
          statuses: null,
          doneStatus: null,
          dueDateAttribute: null,
          assignedToAttribute: null,
          tagAttributes: null,
        }),
      );
    },

    onSelectStatuses: (values: string[]) => {
      const selectedStatuses = statuses.filter((status) =>
        values.includes(status.value.toString()),
      );
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statuses: selectedStatuses,
        }),
      );
    },

    onSelectDoneStatus: (value?: string) => {
      const selectedDoneStatus =
        statuses.find((status) => status.value.toString() === value) ?? null;
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneStatus: selectedDoneStatus,
        }),
      );
    },

    onSelectDueDateAttribute: (metadataId?: string) => {
      const selectedAttribute =
        dateAttributes.find((attribute) => attribute.metadataId === metadataId) ??
        null;
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          dueDateAttribute: selectedAttribute,
        }),
      );
    },

    onSelectAssignedToAttribute: (metadataId?: string) => {
      const selectedAttribute =
        assignedToAttributes.find(
          (attribute) => attribute.metadataId === metadataId,
        ) ?? null;
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          assignedToAttribute: selectedAttribute,
        }),
      );
    },

    onSelectTagAttributes: (metadataIds: string[]) => {
      const selectedAttributes = attributes.filter((attribute) =>
        metadataIds.includes(attribute.metadataId),
      );
      updateConnectionConfiguration<CdsConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          tagAttributes: selectedAttributes,
        }),
      );
    },
  };
};
